import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils/index';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import moment from 'moment';
import 'moment/locale/en-au';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { RequestsToJoinReviewCertificationCertificationReview } from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface CitedDocumentModalTypes {
	closeModal?: closeModalType;
	formId?: string;
	setTitle?: React.Dispatch<React.SetStateAction<string>>;
	canUpdate?: boolean;
	isYou?: boolean;
	update?: () => void;
}

const CitedDocumentModal: React.FC<CitedDocumentModalTypes> = ({
	closeModal,
	formId,
	setTitle,
	canUpdate,
	update,
	isYou
}) => {
	// Get all states from Redux store
	const statesList = useAppSelector((state) => state?.lists.states);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [formType, setFormType] = React.useState<string>('');

	// Set up Form Data state and initial Data
	const initialFormData = {
		id: undefined,
		certificationType: undefined,
		reviewType: undefined,
		displayName: undefined,
		date: undefined,
		expiry: undefined,
		state: undefined,
		country: undefined,
		certificationNumber: undefined,
		confirmed: undefined,
		cited: undefined,
		attachments: []
	};

	const [formData, setFormData] = React.useState<RequestsToJoinReviewCertificationCertificationReview>(initialFormData);

	const getDocumentData = async () => {
		setIsLoading(true);

		const response = await apibridge.getRequestsTojoinReviewCertification(formId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setFormType(response.result.reviewType);

			setTitle!(response.result.displayName);

			setFormData(response.result);
		}
	};

	React.useEffect(() => {
		if (formId) {
			getDocumentData();
		}
		setIsLoading(false);
	}, []);

	// Submit form data to API
	const submitFormData = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();

		setIsLoading(true);

		if (canUpdate && !isYou) {
			const data = {
				certificationReviewId: formData?.id,
				cited: formData.cited,
				confirmed: formData.confirmed
			};

			const response = await apibridge.confirmDocumentsCited(data);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);
				update && update();

				if (closeModal) {
					closeModal();
				}
			}
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}
		}
	};

	return (
		<div className="document-form-modal">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<form>
								{formType !== 'Vaccination' && formType !== 'Qualification' && formType !== 'PoliceCheck' ? (
									<>
										<div className="form-group">
											<label htmlFor="state">State or Territory</label>

											<input
												placeholder="State"
												title="State"
												name="state"
												className="form-control"
												readOnly
												value={
													formData.state && formData.state !== ''
														? statesList?.filter((state) => state.value === formData.state?.toLowerCase())[0].label
														: ''
												}
											/>
										</div>
									</>
								) : null}

								{formType !== 'Vaccination' && formType !== 'Qualification' && formType !== 'PoliceCheck' && (
									<div className="row">
										<div className="form-group col-6">
											<label htmlFor="certificationNumber">{formType === 'Passport' ? 'Document No.' : 'Number'}</label>

											<input className="form-control" type="text" readOnly value={formData.certificationNumber} />
										</div>

										<div className="form-group col-6">
											<label htmlFor="expiry">Expiry</label>

											<input
												className="form-control"
												readOnly
												value={moment(formData.expiry || '')
													.locale('en-au')
													.format('L')}
											/>
										</div>
									</div>
								)}

								{formType === 'Qualification' && (
									<div className="form-group">
										<label htmlFor="certificationType">Qualification</label>

										<input className="form-control" type="text" readOnly value={formData.displayName} />
									</div>
								)}

								{formType === 'Vaccination' && (
									<>
										<div className="form-group">
											<p>
												This is a certificate that shows all record of your vaccination history, which can be downloaded
												via Medicare website.
											</p>
										</div>
									</>
								)}

								{formType === 'PoliceCheck' && (
									<div className="form-group">
										<label htmlFor="date">Date of police check</label>

										<input
											className="form-control"
											type="text"
											readOnly
											value={moment(formData.date || '')
												.locale('en-au')
												.format('L')}
										/>
									</div>
								)}

								<div className="form-group">
									<>
										<label htmlFor="createAttachments">
											{formType === 'Vaccination' || 'Qualification'
												? 'Attachments'
												: 'Photo of registration (optional)'}
										</label>

										{formData.attachments?.map((file, index) => {
											return (
												<div key={file.id + '_' + index} className="download-link">
													<span className="link-name">{file.fileName ? file.fileName : file.id}</span>

													<a href={file.link} download={true} className="link-text d-none d-lg-block">
														Download
													</a>
												</div>
											);
										})}
									</>
								</div>

								{canUpdate && !isYou && (
									<div className="form-group">
										{formType === 'TeacherRegistration' ? (
											<>
												<label className="form-control-checkbox">
													<input
														type="checkbox"
														checked={formData.confirmed}
														onChange={() =>
															setFormData((prevState) => ({
																...prevState,
																confirmed: !formData.confirmed
															}))
														}
													/>
													I have checked with the teacher registration authority and confirmed that this person has a
													valid and current teacher registration.
												</label>

												{/* <label className="form-control-checkbox">
													<input
														type="checkbox"
														checked={formData.notified}
														onChange={() =>
															setFormData((prevState) => ({
																...prevState,
																notified: !formData.notified
															}))
														}
													/>
													I have seen confirmation that this individual has notified the teaching registration authority
													that they are working or volunteering at this organisation.
												</label> */}
											</>
										) : formType === 'WorkingWithChildrenCheck' ? (
											<>
												<label className="form-control-checkbox">
													<input
														type="checkbox"
														checked={formData.confirmed}
														onChange={() =>
															setFormData((prevState) => ({
																...prevState,
																confirmed: !formData.confirmed
															}))
														}
													/>
													I have checked with the Working With Children Check authority and confirmed that this person
													has a valid and current WWCC and is authorised to work or volunteer with children.
												</label>

												{/* <label className="form-control-checkbox">
													<input
														type="checkbox"
														checked={formData.notified}
														onChange={() =>
															setFormData((prevState) => ({
																...prevState,
																notified: !formData.notified
															}))
														}
													/>
													I have seen confirmation that this individual has notified the Working With Children Check
													authority that they are working or volunteering at this organisation.
												</label> */}
											</>
										) : null}

										{/* IMPORTANT NOTE: CLIENT SPECIFICALLY ASKED NOT TO DELETE THESE "SIGHTED FUNCTIONALITY" AND
												ONLY COMMENT THEM OUT */}
										{/* <label className="form-control-checkbox">
											<input
												onChange={() =>
													setFormData((prevState) => ({
														...prevState,
														cited: !formData.cited
													}))
												}
												checked={formData.cited}
												type="checkbox"
											/>
											I have physically sighted this document.
										</label> */}
									</div>
								)}

								<Button
									disabled={isLoading}
									type="submit"
									onClick={(e) => submitFormData(e)}
									className="btn-primary full-width"
								>
									{canUpdate && !isYou ? 'Save and Close' : 'Close'}
								</Button>
							</form>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default CitedDocumentModal;
